import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChildren } from '@angular/core';
import { BehaviorSubject, map, Subject, takeUntil, tap } from 'rxjs';
import { FilterDefinition, FilterItem, FilterItemType, FilterName } from '../../filtering/filter-definition.type';
import { FilterSelectionMap, FilteringHelper } from '../../filtering/filtering.helper';
import { FilteringService } from '../../filtering/filtering.service';
import { SelectItemType } from '../select-with-search/select-item.type';
import { SelectWithSearchComponent } from '../select-with-search/select-with-search.component';

@Component({
  selector: 'shared-filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  standalone: false,
})
export class FilterPanelComponent implements OnInit, OnChanges, OnDestroy {
  private readonly filteringService = inject(FilteringService);

  @Input() disabled = false;
  @Input() showSearch: boolean;
  @Input() set searchValue(value: string) {
    this._searchValue = value;
    // Only auto-show the search input if there's an initial value, but don't hide it when cleared
    if (this.showSearch && value && value !== '') {
      this.displaySearchInput = true;
    }
  }
  get searchValue(): string {
    return this._searchValue;
  }
  @Input() searchPlaceholder = 'Search...';
  @Input() items: FilterItem[] = [];
  @Output() filterChanged = new EventEmitter<FilterDefinition>();
  @Output() filterInit = new EventEmitter<FilterDefinition>();

  @ViewChildren(SelectWithSearchComponent) filterComponents: SelectWithSearchComponent[];

  readonly FilterItemType = FilterItemType;
  filterDefinition$ = new BehaviorSubject<FilterDefinition>(null);
  filterSelectionMap: FilterSelectionMap = new Map();
  displaySearchInput = false;
  isInitializing = true;

  private _searchValue = '';
  private isClearingFilters = false;
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.filteringService.filterChanged$
      .pipe(
        tap(args => (this.filterSelectionMap = FilteringHelper.createFilterSelectionMap(args.filter))),
        map(args => args.filter),
        tap(filter => {
          this.filterDefinition$.next(filter);
          if (this.isInitializing) {
            this.isInitializing = false;
            this.filterInit.emit(filter);
            return;
          }
          if (this.isClearingFilters === false) {
            this.filterChanged.emit(filter);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items || changes.showSearch) {
      this.initializeFiltersWithSearch();
    }

    if (changes.showSearch && this._searchValue != null && this._searchValue.length > 0) {
      this.displaySearchInput = this.showSearch;
    }
  }

  private initializeFiltersWithSearch(): void {
    if (!this.items) {
      return;
    }

    const filtersToInitialize = [...this.items];

    // Automatically add search filter if showSearch is true and not already present
    if (this.showSearch) {
      const hasSearchFilter = filtersToInitialize.some(f => f.name === FilterName.Search);
      if (!hasSearchFilter) {
        const searchFilter: FilterItem = {
          type: FilterItemType.Simple,
          name: FilterName.Search,
          value: this._searchValue || '',
          defaultValue: this._searchValue || '',
          queryParamName: FilterName.Search,
        };
        filtersToInitialize.push(searchFilter);
      }
    }

    this.filteringService.initializeFilters(filtersToInitialize);
  }

  async onFilterValueChanged(selectedIds: string | string[], filter: FilterItem): Promise<void> {
    if (filter.type !== FilterItemType.Select) {
      return;
    }

    const selectedId = Array.isArray(selectedIds) ? selectedIds[0] : selectedIds;
    if (selectedId === FilteringHelper.AllItemsId) {
      await this.filteringService.clearFilter(filter.name);
      return;
    }

    await this.filteringService.selectFilterValue(filter.name, selectedId);
  }

  async onFilterClosed(selectedIds: string | string[], filter: FilterItem): Promise<void> {
    if (filter.type !== FilterItemType.MultiSelect) {
      return;
    }

    const idsArray = Array.isArray(selectedIds) ? selectedIds : [selectedIds];

    // Check if all regular items are selected (excluding auto-added SelectItemType.All items)
    const mapData = this.filterSelectionMap.get(filter.name);
    const allItemIds = mapData?.items.map(i => i.id) ?? [];

    // Filter out SelectItemType.All items from selection as they're auto-added by code
    const fixedAllItemIds = filter.fixedItems?.filter(fi => fi.type === SelectItemType.All).map(fi => fi.id) ?? [];
    const userSelectedIds = idsArray.filter(id => !fixedAllItemIds.includes(id));

    const isAllSelected = allItemIds.length > 0 && allItemIds.every(id => userSelectedIds.includes(id));

    if (filter.clearFilterIfAllSelected && isAllSelected) {
      await this.filteringService.clearFilter(filter.name);
      return;
    }

    await this.filteringService.selectFilterValue(filter.name, idsArray);
  }

  canClearAllFilters(): boolean {
    if (this.searchValue && this.searchValue !== '') {
      return true;
    }
    if (this.filterComponents?.some(c => c.canResetToDefault)) {
      return true;
    }
    const filterDef = this.filterDefinition$.value;
    return filterDef?.filters.some(f => f.type === FilterItemType.Checkbox && f.value !== f.defaultValue) ?? false;
  }

  async clearFilters(): Promise<void> {
    this.isClearingFilters = true;
    try {
      this.searchValue = '';
      this.displaySearchInput = false;

      this.filterComponents.filter(c => c.canResetToDefault).forEach(c => c.resetToDefault());

      // Reset checkbox filters via the service to ensure proper state management
      const checkboxFiltersToReset = this.items.filter(
        item => item.type === FilterItemType.Checkbox && item.value !== item.defaultValue
      );
      for (const item of checkboxFiltersToReset) {
        await this.filteringService.resetToDefault(item.name);
      }

      await this.filteringService.clearFilter(FilterName.Search);
      this.filterChanged.emit(this.filterDefinition$.value);
    } finally {
      this.isClearingFilters = false;
    }
  }

  async onCheckboxChanged(checked: boolean, filter: FilterItem): Promise<void> {
    if (filter.type !== FilterItemType.Checkbox) {
      return;
    }
    await this.filteringService.setFilterValue(filter.name, checked);
  }

  toggleSearch(): void {
    this.displaySearchInput = !this.displaySearchInput;
  }

  async onSearchValueChange(value: string): Promise<void> {
    this.searchValue = value;
    const trimmedValue = value?.trim() ?? '';
    await this.filteringService.setFilterValue(FilterName.Search, trimmedValue);
  }
}
